<script setup>
// The treatment category chip row, shared by the search results page (via TypeFilter) and
// the Full Product Category listing section. Extracted rather than copied so the two cannot
// drift: see shared/treatmentCategories.js for which categories reach it.
//
// Each chip is a filter-mode Chip: a real radio grouped by `name`, styled by
// components/chip.scss (the checked look comes from :has(input:checked), the focus cue and
// keyboard behaviour from the native control). Exclusive selection with an "All" chip as the
// clear mechanism matches the chip's own radio-group guidance, since a radio cannot be
// un-picked by clicking it again.
//
// This component carries no styling: .type_filter lives in components/product-listing.scss
// with the rest of the listing styling.
import { computed } from 'vue';
import Chip from './Chip.vue';

const props = defineProps({
    // { id, name, icon, count }, where icon is an App\Enums\Icon value or null - it is
    // admin-chosen. Already filtered by the caller, so an empty list means "no row".
    categories: {
        type: Array,
        default: () => [],
    },
    // null = show every category
    activeCategory: {
        type: [Number, null],
        default: null,
    },
    // The count on the clear chip: every result the row is narrowing.
    totalCount: {
        type: Number,
        default: 0,
    },
    // The radio group's name. Must be unique per page - Chip.vue's own note - so a page
    // carrying two of these rows has to pass a distinct one for each.
    name: {
        type: String,
        default: 'category-filter',
    },
    allLabel: {
        type: String,
        default: 'All Treatments',
    },
});

const emit = defineEmits(['update:activeCategory']);

// A radio's value must be a string, so the null "all" key maps to '' at this boundary and
// back again on the way out - the parent still sees null, and the ids are numbers.
const selected = computed({
    get: () => props.activeCategory === null ? '' : String(props.activeCategory),
    set: (value) => emit('update:activeCategory', value === '' ? null : Number(value)),
});
</script>

<template>
    <!-- Category icons are admin-chosen and optional (Chip drops the icon when the name is
         null), so a category nobody has picked one for still renders. The clear chip has
         none by design - it is the row's clear, not a category. -->
    <div class="type_filter">
        <Chip
            v-model="selected"
            radio
            :name="name"
            value=""
            :label="allLabel"
            :count="totalCount"
        />
        <Chip
            v-for="category in categories"
            :key="category.id"
            v-model="selected"
            radio
            :name="name"
            :value="String(category.id)"
            :label="category.name"
            :icon="category.icon"
            :count="category.count"
        />
    </div>
</template>
